import {
    getGetBoundaryUrl,
    getUpdateBoundaryUrl
} from 'generatedTypes/flight/services/boundary-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { BOUNDARIES_API } from './constants';
import type { BoundaryResponse, CreateBoundary, FetchBoundary, UpdateBoundary } from './types';

const useBoundary = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchBoundary: FetchBoundary = async ({ id }) => {
        try {
            setIsLoading(true);
            const url = getGetBoundaryUrl(id);

            return await withRetry(
                () => authenticatedGet<BoundaryResponse>(url),
                CONNECTION_ERROR,
                () => fetchBoundary({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const createBoundary: CreateBoundary = async ({ boundary }) => {
        try {
            setIsLoading(true);

            return await withRetry(
                () => authenticatedPost<BoundaryResponse>(BOUNDARIES_API, JSON.stringify(boundary)),
                SAVE_FAILED,
                () => createBoundary({ boundary })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateBoundary: UpdateBoundary = async ({ boundary, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateBoundaryUrl(id);

            return await withRetry(
                () => authenticatedPut<BoundaryResponse>(url, JSON.stringify(boundary)),
                SAVE_FAILED,
                () => updateBoundary({ boundary, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createBoundary,
        fetchBoundary,
        isLoading,
        updateBoundary
    };
};

export default useBoundary;
